import { sortBy } from "lodash";
import moment from "moment";
import { ReactNode } from "react";
import {
  ComplianceProgramAppliesTo,
  ComplianceProgramRecurrence,
  ComplianceProgramState,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import { stepNames } from "@apps/programs/components/compliance-program-timeline-vocabulary";
import { formatNameList, getAppliesToLabel } from "@apps/programs/helpers";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";

import { formatDateRange, stepOrder } from "../helpers";
import { ProgramForm } from "../types";
import useProgramFormLoadPreview from "../use-program-form-load-preview";
import { plural } from "./readiness-figures";

/** More names than this read as a list to scan, not a sentence. */
const maxNames = 3;

const formatDate = (date: string) => moment(date).format("D MMM YYYY");

const recurrenceText: Partial<Record<ComplianceProgramRecurrence, string>> = {
  [ComplianceProgramRecurrence.Monthly]: "Repeats monthly",
  [ComplianceProgramRecurrence.Quarterly]: "Repeats quarterly",
  [ComplianceProgramRecurrence.Yearly]: "Repeats yearly",
};

/** "Ana, Ben, Cleo and 4 more", so a long selection stays one line. */
const shortNameList = (names: string[]) =>
  names.length <= maxNames + 1
    ? formatNameList(names)
    : `${names.slice(0, maxNames).join(", ")} and ${names.length - maxNames} more`;

/**
 * One fact about the review, on a card of its own: the headline figure, and the
 * detail behind it underneath. A description list, so a screen reader reads each
 * title with its value.
 */
const Tile = ({
  icon,
  tone,
  title,
  value,
  detail,
}: {
  icon: ReactNode;
  /** The colours of the circle behind the icon. */
  tone: string;
  title: string;
  value: ReactNode;
  detail: ReactNode;
}) => (
  <div className="rounded-md border bg-white p-5">
    {/* The icon says what the card is; the title is there for screen
        readers only. */}
    <dt className="flex items-center">
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${tone}`}
      >
        {icon}
      </span>
      <span className="sr-only">{title}</span>
    </dt>
    <dd className="mt-3">
      <div className="text-base font-medium text-gray-900">{value}</div>
      {detail && <div className="mt-0.5 text-gray-600">{detail}</div>}
    </dd>
  </div>
);

/**
 * The review at a glance before it is published: its name, when it runs, which
 * steps it has and how many people take part. Read from the form, except the
 * numbers of people, which only the backend can count.
 */
export const ProgramFormSummaryCard = ({
  proposedComplianceProgram: program,
  creator,
  participantCount,
  writerCount,
  countIsCurrent = true,
  canPublish = true,
}: {
  proposedComplianceProgram: ProgramForm;
  creator: BasicUser;
  /** Undefined until the backend has counted them. */
  participantCount?: number;
  /**
   * How many people write a review, which the time figures are per. A manager
   * who is not reviewed still writes, so it can differ from the participants.
   */
  writerCount?: number;
  /**
   * False while the count is for the form before its last edit, so the heading
   * waits for the new one before it claims the draft is ready.
   */
  countIsCurrent?: boolean;
  /** False while publishing would be refused, so the heading does not claim it. */
  canPublish?: boolean;
}) => {
  const label = useLabel();
  const names = stepNames(label);
  const steps = sortBy(program.steps, (step) => stepOrder[step.stepType]);

  // Zero is missing here, as it is to the Timeline and to publishing: the form
  // does not let a step open on the day somebody joins. A due date on or before
  // the kickoff is not one either.
  const kickoffAfter = program.startDateOffset;
  const dueAfterKickoff =
    program.startDateDueDateOffset && kickoffAfter
      ? program.startDateDueDateOffset - kickoffAfter
      : 0;
  // A conditional review has no dates to put in the tile's headline, so it says
  // what kind it is there, and how its dates are worked out under it.
  const [dates, datesDetail] = program.ongoing
    ? [
        "Conditional",
        !kickoffAfter
          ? "Kickoff and due dates not set."
          : dueAfterKickoff <= 0
            ? `Kicks off ${plural(kickoffAfter, "day")} after each person's start date. Due date not set.`
            : `Kicks off ${plural(kickoffAfter, "day")} after each person's start date, due ${plural(dueAfterKickoff, "day")} later.`,
      ]
    : program.startDate && program.dueDate
      ? [
          formatDateRange(program.startDate, program.dueDate),
          recurrenceText[program.recurrence] ?? "Runs once",
        ]
      : ["Not set", "Kickoff and due dates not set."];

  const selection =
    program.appliesTo === ComplianceProgramAppliesTo.Departments
      ? program.appliesToTeams.map((team) => team.title)
      : program.appliesTo === ComplianceProgramAppliesTo.ReportsTo
        ? program.appliesToManagers.map((manager) => manager.name)
        : program.appliesTo === ComplianceProgramAppliesTo.Users
          ? program.appliesToUsers.map((user) => user.name)
          : [];
  const exclusions = [
    program.excludedUsers.length > 0 &&
      `leaves out ${shortNameList(program.excludedUsers.map((user) => user.name))}`,
    program.excludedHireDateAfter &&
      `leaves out people who start after ${formatDate(program.excludedHireDateAfter)}`,
  ].filter(Boolean);
  const rule = (
    <>
      {getAppliesToLabel(
        label,
        program.appliesTo,
        creator,
        currentUserVar().id,
      )}
      {selection.length > 0 && `: ${shortNameList(selection)}`}
    </>
  );
  const ruleExclusions = exclusions.length > 0 && (
    <span className="text-gray-500"> ({exclusions.join("; ")})</span>
  );

  return (
    // The review's name is the section's heading, and each fact is a card of
    // its own, like the cards in the sections under it.
    <div className="text-sm">
      {/* A draft that can go out says so. A draft that cannot yet, and a
          review that is already out, are only named. A rule that matches
          nobody would be refused, so it is not ready either, and nor is one
          whose count is not known yet or is for an earlier edit. */}
      <h2 className="text-lg font-semibold">
        {program.state === ComplianceProgramState.Draft &&
        canPublish &&
        steps.length > 0 &&
        participantCount !== undefined &&
        participantCount > 0 &&
        countIsCurrent
          ? `${program.title || "Untitled"} Is Ready to Publish`
          : program.title || "Untitled"}
      </h2>
      <dl className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Tile
          icon={<TopicflowIcon type="calendar" className="h-4 w-4" />}
          tone="bg-blue-50 text-blue-600"
          title="Dates"
          value={dates}
          detail={datesDetail}
        />
        <Tile
          icon={<TopicflowIcon type="route" className="h-4 w-4" />}
          tone="bg-violet-50 text-violet-600"
          title="Steps"
          value={
            steps.length > 0 ? plural(steps.length, "step") : "No steps yet"
          }
          detail={
            steps.length > 0
              ? steps.map((step) => names[step.stepType]).join(" · ")
              : "Turn a step on in the Workflow tab."
          }
        />
        {/* The number of people is the headline once it is known. Until then
            the rule takes its place, rather than a number that may be wrong. */}
        <Tile
          icon={<TopicflowIcon type="users" className="h-4 w-4" />}
          tone="bg-emerald-50 text-emerald-600"
          title="Participants"
          value={
            participantCount === undefined
              ? rule
              : plural(participantCount, "person", "people")
          }
          detail={
            participantCount === undefined ? (
              ruleExclusions
            ) : (
              <>
                {rule}
                {ruleExclusions}
                {/* The time figures below are per person who writes, so a
                    different count is said here rather than left to puzzle. */}
                {writerCount !== undefined &&
                  writerCount !== participantCount && (
                    <div className="mt-1 text-gray-500">
                      {plural(writerCount, "person", "people")}{" "}
                      {writerCount === 1 ? "writes" : "write"}{" "}
                      {label("review", { pluralize: true })}
                    </div>
                  )}
              </>
            )
          }
        />
      </dl>
    </div>
  );
};

/**
 * The summary card with the number of people in it. Asks with the same
 * variables as the Readiness figures under it, so Apollo sends one request.
 */
const ProgramFormSummary = (props: {
  proposedComplianceProgram: ProgramForm;
  creator: BasicUser;
  canPublish: boolean;
}) => {
  const { preview, waitingForASelection, current } = useProgramFormLoadPreview(
    props.proposedComplianceProgram,
  );
  return (
    <ProgramFormSummaryCard
      {...props}
      participantCount={
        waitingForASelection ? undefined : preview?.participantCount
      }
      writerCount={waitingForASelection ? undefined : preview?.writers.length}
      countIsCurrent={current}
    />
  );
};

export default ProgramFormSummary;
